// client/src/utils/equityBaseline.js
// 「本金變化曲線」（StrategyKPICharts Row 4）用的純函式。沒有 React、沒有副作用，可以直接用 node 測。
//
// ⚠️ 2026-09-29（A10）：報表的 annual_pnl_usd **不是每期損益**，是「每期期末的已平倉餘額」
//   ＝ 初始本金 ＋ 到該桶結束為止的累積已實現損益（mas_lib/mas/backtest/backtest_engine.py calculate_kpi，
//   自 2025-07-26 起一直是這個語意）。不含未平倉部位的浮動損益，也看不到期間內的回撤。
//   舊圖把柱從 0 畫起、正數一律主色橘 → 虧到只剩 5,100 看起來像「賺了 5,100」
//   （dev DB 12 份報表 47 根柱，有 27 根低於本金，其中 12 根「虧損但餘額仍為正」被畫成跟獲利一樣）。
//   改成從「初始本金」基準線起算：高於本金往上長、低於本金往下垂 —— 位置本身就說明賺賠，不只靠顏色。
//   ⚠️ 柱子（位置、顏色）表示的是「期末餘額相對初始本金」，不是「這一期賺或賠」：虧損後回升的年份仍是紅柱
//   （dev DB 47 根裡有 6 根顏色跟當期賺賠相反）。所以 tooltip 另外列「本期」損益（change），說明文字也寫明。
//
// 報表本身沒有「初始本金」欄位（策略管理有 strategy.capital，但市集、Beast 都拿不到），
//   所以從報表反推，而且至少要有兩個條件互相驗證才採用（見 deriveInitialCapital）。拿不到就退回中性顯示，絕不猜。
//
// 時間桶（period）是依「已平倉交易的 exit_time」分的，不是日曆：區間 ≥365 天用年、90–364 天用月、
//   14–89 天用 ISO 週、<14 天用日；沒有平倉交易的桶不會出現。

/** 各估計值與平均的最大容許誤差：max(0.5 USD, 本金 × 0.01%) */
const ABS_TOLERANCE = 0.5;
const REL_TOLERANCE = 1e-4;
/** 累積報酬在 −100% 附近時 1＋r/100 趨近 0，除下去不可靠 → 這一桶不拿來估計，只拿來驗證 */
const MIN_GROWTH_FACTOR = 0.01;
/** 至少要有幾個互相對得上的條件（估計值＋驗證）才採用反推的本金 */
const MIN_CONSTRAINTS = 2;

/**
 * 把報表裡的序列統一成 [{ key, value }]。兩種形狀都吃：
 *   - 陣列 [{ period | date, value }]（後端各 controller / beastEngine.formatReport 轉過的）
 *   - Python 原始物件 { [innerKey]: { k: v } } 或直接 { k: v }（2026-04-13 以前的 Beast 舊資料）
 * value 一律轉成 Number；轉不了的是 NaN，由呼叫端決定怎麼處理（不在這裡偷偷變 0）。
 */
export function toSeries(raw, innerKey) {
  if (Array.isArray(raw)) {
    return raw.map((item) => ({
      key: String(item?.period ?? item?.date ?? ""),
      value: toNumber(item?.value),
    }));
  }
  if (raw && typeof raw === "object") {
    const inner = innerKey && raw[innerKey] && typeof raw[innerKey] === "object" ? raw[innerKey] : raw;
    return Object.entries(inner).map(([key, value]) => ({ key: String(key), value: toNumber(value) }));
  }
  return [];
}

/** null / undefined / "" / 非數字 → NaN（注意 Number(null) 是 0，不能直接用） */
function toNumber(v) {
  if (v === null || v === undefined || v === "") return NaN;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : NaN;
}

export const round2 = (v) => Math.round(v * 100) / 100;

/**
 * 從報表反推初始本金。兩個來源：
 *   (a) 逐桶：本金 ＝ 餘額_i ÷ (1 ＋ 累積報酬_i ÷ 100)（累積報酬＝cumulative_return_line_chart，同一組桶、鍵名一致）
 *   (b) 本金 ＝ 最後一桶餘額 − general_data.total_profit_loss（助理／管理／市集／Beast 有；公開 API 沒有）
 * 採用條件：
 *   - 所有估計值都落在平均值 ± max(0.5 USD, 本金 × 0.01%) 內，且本金 > 0；
 *   - 累積報酬在 −100% 附近（|1＋r/100| < 0.01）的桶不拿來估計，改成驗證：本金 ×(1＋r/100) 與餘額相差在同一容許值內；
 *   - 至少要有兩個條件（估計值＋驗證）互相對得上 —— 只有一個估計值時「偏離平均」一定通過，等於沒檢查
 *     （例：只有一桶、沒有 general_data，「每期損益」語意的 2,000／+20% 會被反推成本金 1,666.67）。
 * 這個一致性檢查也會擋掉語意不是餘額的資料（例如 DEMO 舊版 fakeKPI 是「每年損益」，各桶反推值互相矛盾）。
 * @returns {number|null} 四捨五入到分；拿不到回 null
 */
export function deriveInitialCapital(result) {
  const balances = toSeries(result?.annual_pnl_usd, "annual_pnl_usd").filter((p) => Number.isFinite(p.value));
  if (!balances.length) return null;

  const cumByKey = new Map(
    toSeries(result?.cumulative_return_line_chart, "cumulative_return_percent")
      .filter((p) => Number.isFinite(p.value))
      .map((p) => [p.key, p.value])
  );

  const estimates = [];
  const nearWipeout = []; // { balance, growth }：不拿來估計，只拿來驗證
  for (const b of balances) {
    if (!cumByKey.has(b.key)) continue;
    const growth = 1 + cumByKey.get(b.key) / 100;
    if (Math.abs(growth) < MIN_GROWTH_FACTOR) nearWipeout.push({ balance: b.value, growth });
    else estimates.push(b.value / growth);
  }

  const generalData = result?.general_data;
  const totalPnl = generalData && typeof generalData === "object" ? toNumber(generalData.total_profit_loss) : NaN;
  if (Number.isFinite(totalPnl)) estimates.push(balances[balances.length - 1].value - totalPnl);

  if (!estimates.length) return null;
  if (estimates.length + nearWipeout.length < MIN_CONSTRAINTS) return null;
  const mean = estimates.reduce((sum, e) => sum + e, 0) / estimates.length;
  if (!(mean > 0)) return null;
  const tolerance = Math.max(ABS_TOLERANCE, mean * REL_TOLERANCE);
  if (estimates.some((e) => Math.abs(e - mean) > tolerance)) return null;
  if (nearWipeout.some(({ balance, growth }) => Math.abs(mean * growth - balance) > tolerance)) return null;
  return round2(mean);
}

/**
 * 把報表的餘額序列等比例縮放（回傳新物件，不改原本的）。DEMO 用：fakeKPI 是以本金 10,000 做的，
 *   使用者在表單填 50,000 時乘 5 → 圖上反推出的初始本金＝表單的本金，不會一邊寫 50,000、一邊寫 10,000。
 *   百分比類欄位（累積報酬、回撤、勝率）與本金無關，不用動。
 */
export function rescaleBalances(result, factor) {
  if (!result || typeof result !== "object" || !Number.isFinite(factor) || !(factor > 0) || factor === 1) return result;
  const scaled = { ...result };
  if (Array.isArray(result.annual_pnl_usd)) {
    scaled.annual_pnl_usd = result.annual_pnl_usd.map((item) => {
      const v = toNumber(item?.value);
      return Number.isFinite(v) ? { ...item, value: round2(v * factor) } : item;
    });
  }
  const generalData = result.general_data;
  if (generalData && typeof generalData === "object" && Number.isFinite(toNumber(generalData.total_profit_loss))) {
    scaled.general_data = { ...generalData, total_profit_loss: round2(toNumber(generalData.total_profit_loss) * factor) };
  }
  return scaled;
}

/**
 * 柱子的語意分類（顏色由呼叫端對應 token）：
 *   "above"   餘額 ≥ 初始本金（相等歸這邊，與同卡「總報酬率 ≥ 0 用主色」一致）
 *   "below"   餘額 < 初始本金
 *   "neutral" 拿不到初始本金 → 不分賺賠（不用獲利色，避免暗示有賺）
 * 以「分」為單位比較，避免 9999.999999 這種浮點雜訊被判成低於本金。
 */
export function equityTone(balance, capital) {
  if (!Number.isFinite(capital) || !(capital > 0) || !Number.isFinite(balance)) return "neutral";
  return Math.round(balance * 100) >= Math.round(capital * 100) ? "above" : "below";
}

/**
 * 產生圖表用的資料。
 * @returns {{ capital: number|null, bars: Array<{
 *   name: string,        // 時間桶
 *   balance: number,     // 餘額（USD，四捨五入到分）
 *   range: [number, number] | null, // [本金, 餘額]：recharts 的範圍柱，從本金線長到餘額；拿不到本金時為 null
 *   tone: "above"|"below"|"neutral",
 *   diff: number|null,   // 餘額 − 本金
 *   pct: number|null,    // 相對本金 %：優先用同一桶的累積報酬（與上一張圖同一個數字），沒有才自己算
 *   change: number|null, // 本期損益＝本桶餘額 − 上一桶餘額（第一桶用本金；拿不到本金時第一桶為 null）。
 *                        //   桶只在有平倉交易時出現，已平倉餘額在兩桶之間不會變，所以這就是該期的已實現損益。
 * }> }}
 */
export function buildEquityBars(result) {
  const capital = deriveInitialCapital(result);
  const cumByKey = new Map(
    toSeries(result?.cumulative_return_line_chart, "cumulative_return_percent")
      .filter((p) => Number.isFinite(p.value))
      .map((p) => [p.key, p.value])
  );
  let prev = capital;
  const bars = toSeries(result?.annual_pnl_usd, "annual_pnl_usd")
    .filter((p) => Number.isFinite(p.value))
    .map((p) => {
      const balance = round2(p.value);
      const change = prev === null ? null : round2(p.value - prev);
      prev = p.value;
      if (capital === null) {
        return { name: p.key, balance, range: null, tone: "neutral", diff: null, pct: null, change };
      }
      const pct = cumByKey.has(p.key) ? round2(cumByKey.get(p.key)) : round2(((p.value - capital) / capital) * 100);
      return {
        name: p.key,
        balance,
        range: [capital, balance],
        tone: equityTone(p.value, capital),
        diff: round2(p.value - capital),
        pct,
        change,
      };
    });
  return { capital, bars };
}

/**
 * 6792.4 → "6,792.40"（固定用 en-US 千分位，跟語系無關，與圖上其他數字一致）。
 * 負數用 U+2212「−」，跟 formatSigned 一致（同一個 tooltip 不會一個「-262.00」、一個「−10,262.00」）。
 * dropZeroCents：整數時不帶小數（本金 10000 → "10,000"）。
 */
export function formatUsd(v, { dropZeroCents = false } = {}) {
  if (!Number.isFinite(v)) return "-";
  const digits = dropZeroCents && Math.round(v * 100) % 100 === 0 ? 0 : 2;
  const text = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const rounded = digits ? Math.round(v * 100) : Math.round(v);
  return rounded < 0 ? `−${text}` : text;
}

/**
 * 帶正負號：+1,234.00 ／ −3,207.60（負號用 U+2212，與文案一致；0 不帶號）。
 * signOf：四捨五入後變 0 時改用這個值決定正負號 —— 餘額 +0.10 的柱，% 顯示「+0.00%」而不是沒有號的「0.00%」。
 */
export function formatSigned(v, { suffix = "", signOf } = {}) {
  if (!Number.isFinite(v)) return "-";
  const r = round2(v);
  const basis = r === 0 && Number.isFinite(signOf) ? round2(signOf) : r;
  const sign = basis > 0 ? "+" : basis < 0 ? "−" : "";
  return `${sign}${formatUsd(Math.abs(r))}${suffix}`;
}

/**
 * Y 軸刻度單位：依整張圖的最大絕對值決定，同一軸所有刻度用同一個單位
 * （逐刻度各自判斷會出現「10.5K／7,000／3,500」混用）。
 * @returns {"" | "K" | "M"}
 */
export function axisUnitFor(values) {
  const finite = (values || []).filter((v) => Number.isFinite(v)).map((v) => Math.abs(v));
  const maxAbs = finite.length ? Math.max(...finite) : 0;
  if (maxAbs >= 1e6) return "M";
  if (maxAbs >= 1e4) return "K";
  return "";
}

/**
 * Y 軸刻度：unit="K" → 10,500 "10.5K"、8,250 "8.25K"、−3,500 "-3.5K"、1,000,000 "1,000K"；
 *   unit="M" → "1.25M"；unit="" → "5,100"（避免長數字被裁掉）。
 * 小數最多兩位：recharts 的刻度常是 2,750／8,250 這種，只留一位會變成 2.8K／8.3K，跟實際刻度對不上。
 * 軸上用 ASCII「-」：跟同元件其他圖（recharts 預設刻度）一致。
 */
export function formatAxisUsd(v, unit = "") {
  if (!Number.isFinite(v)) return "";
  if (v === 0) return "0";
  const scaled = (divisor, suffix) =>
    Math.round((v / divisor) * 100) === 0 // 捨入成 0 → 「0」，不要「-0M」「0K」
      ? "0"
      : `${(v / divisor).toLocaleString("en-US", { maximumFractionDigits: 2 })}${suffix}`;
  if (unit === "M") return scaled(1e6, "M");
  if (unit === "K") return scaled(1e3, "K");
  return Math.round(v).toLocaleString("en-US");
}
